import { registerUploadFilesApi, getUploadFileUrlsApi, getUploadPartUrlsApi, updateUploadFileStatusApi, restartUploadFileApi, reportUploadFileProgressApi, normalizeUploadPath, toNFC, UPLOAD_TYPE } from './apiServices';
import { updateLiveUpload, withUploadLock, waitForOnline, LIVE_STATUS } from './liveUploads';

// Files are registered and signed in batches so large uploads don't fire thousands of calls at once
const BATCH_SIZE = 500;
const UPLOAD_CONCURRENCY = 6;
// Multipart: parts uploaded in parallel, URLs signed in small groups so they don't expire
const PART_CONCURRENCY = 4;
const PART_SIGN_BATCH = 20;
const MAX_ATTEMPTS = 3;
// A PUT with no progress for this long is aborted and retried
const STALL_TIMEOUT_MS = 60 * 1000;
// Rounds of re-uploading parts the server reports missing
const MAX_COMPLETE_ROUNDS = 3;
// Re-sign a URL this long before it expires
const URL_EXPIRY_MARGIN_MS = 60 * 1000;
const DONE_STATUSES = ['COMPLETED', 'UPLOADED'];
// Throttle for UI progress updates
const PUBLISH_INTERVAL_MS = 300;
const SPEED_SAMPLE_MS = 1000;
// A longer gap between samples means the computer slept or the tab was frozen, so speed starts over
const SPEED_RESET_MS = 10 * 1000;
// Each in-flight file reports progress this often so others can see the upload is still running
const HEARTBEAT_INTERVAL_MS = 30 * 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Toast text for a finished run, including the most common failure reason
export const describeUploadResult = (result) => {
  if (result.alreadyRunning) return { message: 'This upload is already running in another tab', variant: 'warning' };
  if (!result.failedFiles && !result.skippedFiles) {
    return { message: `${result.completedFiles.toLocaleString()} file${result.completedFiles === 1 ? '' : 's'} uploaded successfully`, variant: 'success' };
  }
  const parts = [`${result.completedFiles.toLocaleString()} uploaded`];
  if (result.failedFiles) parts.push(`${result.failedFiles.toLocaleString()} failed`);
  if (result.skippedFiles) parts.push(`${result.skippedFiles.toLocaleString()} skipped`);
  const counts = new Map();
  [...result.failures.map(entry => entry.error), ...result.skipped.map(entry => entry.reason)]
    .filter(Boolean)
    .forEach(reason => counts.set(reason, (counts.get(reason) || 0) + 1));
  const reasons = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([reason]) => reason);
  let detail = '';
  if (reasons.length === 1) detail = ` (${reasons[0]})`;
  else if (reasons.length > 1) detail = ` (${reasons[0]}, and ${reasons.length - 1} other reason${reasons.length > 2 ? 's' : ''})`;
  return { message: `Upload finished: ${parts.join(', ')}${detail}`, variant: result.completedFiles ? 'warning' : 'error' };
};

const partSizeOf = (file, partSize, n) => Math.max(0, Math.min(partSize, file.size - (n - 1) * partSize));

const missingPartsOf = (error) => (error && error.data && Array.isArray(error.data.missing_parts) ? error.data.missing_parts.map(Number) : []);

// Retries with backoff (~1s, 2s, 4s). Time offline or paused doesn't use up attempts,
// and missing_parts errors are returned straight away.
const withRetry = async (task, control) => {
  let lastError;
  let attempt = 0;
  while (attempt < MAX_ATTEMPTS) {
    if (control) await untilRunning(control);
    await waitForOnline();
    try {
      return await task(attempt);
    } catch (error) {
      lastError = error;
      if (missingPartsOf(error).length) throw error;
      if (error.paused || (control && control.paused)) continue;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) continue;
      attempt += 1;
      if (attempt < MAX_ATTEMPTS) await sleep(1000 * 2 ** (attempt - 1) + Math.random() * 300);
    }
  }
  throw lastError;
};

// Pause / resume per upload_id. Pausing aborts in-flight PUTs; finished parts are kept.
const controls = new Map();
const createControl = (uploadId) => {
  const control = { paused: false, waiters: [], xhrs: new Set() };
  controls.set(uploadId, control);
  return control;
};
const untilRunning = (control) => (control.paused ? new Promise(resolve => control.waiters.push(resolve)) : Promise.resolve());

export const pauseStreamUpload = (uploadId) => {
  const control = controls.get(uploadId);
  if (!control || control.paused) return false;
  control.paused = true;
  control.xhrs.forEach(xhr => {
    xhr.pausedAbort = true;
    xhr.abort();
  });
  updateLiveUpload(uploadId, { status: LIVE_STATUS.PAUSED, speed: 0 });
  return true;
};

export const resumeStreamUpload = (uploadId) => {
  const control = controls.get(uploadId);
  if (!control || !control.paused) return false;
  control.paused = false;
  control.waiters.splice(0).forEach(resolve => resolve());
  updateLiveUpload(uploadId, { status: LIVE_STATUS.UPLOADING });
  return true;
};

const isExpiring = (signed) => !!signed.expiresAt && Date.now() > signed.expiresAt - URL_EXPIRY_MARGIN_MS;

// XHR because fetch can't report upload progress. Aborted if nothing is sent for STALL_TIMEOUT_MS.
const sendToStorage = ({ url, method = 'PUT', headers = {} }, body, onBytes, control) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  if (control) control.xhrs.add(xhr);
  let lastActivity = Date.now();
  let stalled = false;
  const watchdog = setInterval(() => {
    if (Date.now() - lastActivity > STALL_TIMEOUT_MS) {
      stalled = true;
      xhr.abort();
    }
  }, 5000);
  const settle = (fn, value) => {
    clearInterval(watchdog);
    if (control) control.xhrs.delete(xhr);
    fn(value);
  };
  xhr.open(method, url);
  Object.keys(headers).forEach(name => xhr.setRequestHeader(name, headers[name]));
  xhr.upload.onprogress = (evt) => {
    lastActivity = Date.now();
    onBytes(evt.loaded);
  };
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      settle(resolve);
    } else {
      const error = new Error(`Storage returned HTTP ${xhr.status}`);
      error.status = xhr.status;
      settle(reject, error);
    }
  };
  xhr.onerror = () => settle(reject, new Error('Network error while uploading'));
  xhr.onabort = () => {
    const error = new Error(xhr.pausedAbort ? 'Upload paused' : stalled ? 'Upload stalled: no data sent for 60 seconds' : 'Upload aborted');
    error.paused = !!xhr.pausedAbort;
    settle(reject, error);
  };
  xhr.send(body);
});

// S3 uses HTTP/1.1, and the browser opens at most 6 connections to a host across all its tabs. Extra
// PUTs would wait in the browser's queue sending nothing, so the stall watchdog would abort them and
// uploads in other tabs would starve. Each PUT takes one of these slots first, shared by every tab.
const STORAGE_SLOTS = 6;
const SLOT_PREFIX = 'stream-upload-slot:';
// While every slot is busy, wait on one and look for any free one again this often
const SLOT_RECHECK_MS = 1000;

const withStorageSlot = async (task) => {
  if (typeof navigator === 'undefined' || !navigator.locks) return task();
  for (;;) {
    for (let i = 0; i < STORAGE_SLOTS; i++) {
      let granted = false;
      const result = await navigator.locks.request(SLOT_PREFIX + i, { ifAvailable: true }, lock => {
        if (!lock) return null;
        granted = true;
        return task();
      });
      if (granted) return result;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SLOT_RECHECK_MS);
    let granted = false;
    try {
      const result = await navigator.locks.request(SLOT_PREFIX + Math.floor(Math.random() * STORAGE_SLOTS), { signal: controller.signal }, () => {
        granted = true;
        clearTimeout(timer);
        return task();
      });
      return result;
    } catch (error) {
      if (granted || error.name !== 'AbortError') throw error;
    }
  }
};

// A run paused while waiting for a slot doesn't start its PUT
const putToStorage = (signed, body, onBytes, control) => withStorageSlot(() => {
  if (control && control.paused) {
    const error = new Error('Upload paused');
    error.paused = true;
    throw error;
  }
  return sendToStorage(signed, body, onBytes, control);
});

// Runs tasks with limited concurrency. After a failure no new tasks start; running ones finish first.
export const runPool = async (tasks, concurrency) => {
  let cursor = 0;
  let firstError = null;
  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
    while (cursor < tasks.length && !firstError) {
      const task = tasks[cursor++];
      try {
        await task();
      } catch (error) {
        if (!firstError) firstError = error;
      }
    }
  });
  await Promise.all(workers);
  if (firstError) throw firstError;
};

const chunk = (list, size) => {
  const chunks = [];
  for (let i = 0; i < list.length; i += size) chunks.push(list.slice(i, i + size));
  return chunks;
};

const toFileMeta = ({ file, relativePath }) => ({
  name: toNFC(file.name),
  size: file.size,
  type: file.type || 'application/octet-stream',
  path: relativePath,
});

// Uploads items into an existing session. Safe to re-run: completed files are skipped, failed ones
// retried and large files continue from their uploaded parts. Never rejects; per-file failures are
// returned. Only one tab uploads a session at a time (otherwise resolves with alreadyRunning).
export const runStreamUpload = (params) => withUploadLock(params.uploadId, () => uploadSession(params), () => ({
  totalFiles: params.items.length,
  totalBytes: params.items.reduce((sum, item) => sum + item.file.size, 0),
  completedFiles: 0,
  failedFiles: 0,
  skippedFiles: 0,
  uploadedBytes: 0,
  failures: [],
  skipped: [],
  alreadyRunning: true,
}));

const uploadSession = async ({ assetType, assetSlug, uploadId, items }) => {
  const api = { assetType, assetSlug, uploadId };
  const control = createControl(uploadId);
  const retry = (task) => withRetry(task, control);
  const progress = {
    totalFiles: items.length,
    totalBytes: items.reduce((sum, item) => sum + item.file.size, 0),
    completedFiles: 0,
    failedFiles: 0,
    skippedFiles: 0,
    uploadedBytes: 0,
  };
  const failures = [];
  const skipped = [];
  // Bytes sent so far per in-flight file
  const inFlight = new Map();
  const inFlightItems = new Map();
  // True while files are being registered, shown as "Preparing to upload"
  let preparing = true;
  let registeredFiles = 0;
  let speed = 0;
  let speedSample = { at: Date.now(), bytes: 0 };
  let lastPublish = 0;

  const snapshot = () => {
    let inFlightBytes = 0;
    inFlight.forEach(bytes => { inFlightBytes += bytes; });
    const activeFiles = [];
    inFlightItems.forEach((item, fileId) => activeFiles.push({
      fileId, path: item.relativePath, size: item.file.size, bytes: inFlight.get(fileId) || 0,
    }));
    return { ...progress, uploadedBytes: progress.uploadedBytes + inFlightBytes, activeFiles, failures };
  };
  const publish = (force = false) => {
    const now = Date.now();
    if (!force && now - lastPublish < PUBLISH_INTERVAL_MS) return;
    lastPublish = now;
    const current = snapshot();
    if (now - speedSample.at > SPEED_RESET_MS) {
      speed = 0;
      speedSample = { at: now, bytes: current.uploadedBytes };
    } else if (now - speedSample.at >= SPEED_SAMPLE_MS) {
      const instant = Math.max(0, current.uploadedBytes - speedSample.bytes) / ((now - speedSample.at) / 1000);
      speed = speed ? speed * 0.7 + instant * 0.3 : instant;
      speedSample = { at: now, bytes: current.uploadedBytes };
    }
    updateLiveUpload(uploadId, { ...current, preparing, registeredFiles, speed: control.paused ? 0 : speed, status: control.paused ? LIVE_STATUS.PAUSED : LIVE_STATUS.UPLOADING });
  };
  const fail = (item, reason) => {
    progress.failedFiles += 1;
    failures.push({ path: item.relativePath, size: item.file.size, error: reason || 'Upload failed' });
  };
  const complete = (item) => {
    progress.completedFiles += 1;
    progress.uploadedBytes += item.file.size;
  };

  const fetchFileInfo = async (fileId) => {
    const fresh = await getUploadFileUrlsApi({ ...api, fileIds: [fileId] });
    if (!fresh.urls[fileId]) throw new Error('No upload URL returned');
    return fresh.urls[fileId];
  };

  // Whole file in one PUT; each retry gets a fresh URL
  const uploadSingle = async (fileId, item, initial) => {
    let signed = initial;
    await retry(async (attempt) => {
      if (attempt > 0 || isExpiring(signed)) signed = await fetchFileInfo(fileId);
      inFlight.set(fileId, 0);
      try {
        await putToStorage({ url: signed.url, method: signed.method, headers: signed.headers || {} }, item.file, (bytes) => {
          inFlight.set(fileId, bytes);
          publish();
        }, control);
      } catch (error) {
        inFlight.set(fileId, 0);
        publish();
        throw error;
      }
    });
  };

  // Uploads the given parts of a multipart file
  const uploadParts = async (fileId, item, info, partNumbers, partBytes) => {
    const { partSize } = info;
    const publishFile = () => {
      let sent = 0;
      partBytes.forEach(bytes => { sent += bytes; });
      inFlight.set(fileId, sent);
      publish();
    };
    partNumbers.forEach(n => partBytes.set(n, 0));
    publishFile();

    for (const group of chunk(partNumbers, PART_SIGN_BATCH)) {
      let urls = await retry(() => getUploadPartUrlsApi({ ...api, fileId, partNumbers: group }));
      await runPool(group.map(n => async () => {
        const blob = item.file.slice((n - 1) * partSize, n * partSize);
        await retry(async (attempt) => {
          // Retries and expiring URLs get a freshly signed URL
          if (attempt > 0 || !urls[n] || isExpiring(urls[n])) {
            urls = { ...urls, ...(await getUploadPartUrlsApi({ ...api, fileId, partNumbers: [n] })) };
            if (!urls[n]) throw new Error(`No upload URL returned for part ${n}`);
          }
          try {
            await putToStorage({ url: urls[n].url }, blob, (bytes) => {
              partBytes.set(n, bytes);
              publishFile();
            }, control);
            partBytes.set(n, partSizeOf(item.file, partSize, n));
            publishFile();
          } catch (error) {
            partBytes.set(n, 0);
            publishFile();
            const partError = new Error(`Part ${n} failed: ${error.message}`);
            partError.paused = error.paused;
            throw partError;
          }
        });
      }), PART_CONCURRENCY);
    }
  };

  const uploadMultipart = async (fileId, item, info, partBytes) => {
    const done = new Set(info.partsDone);
    done.forEach(n => partBytes.set(n, partSizeOf(item.file, info.partSize, n)));
    const todo = [];
    for (let n = 1; n <= info.totalParts; n++) if (!done.has(n)) todo.push(n);
    await uploadParts(fileId, item, info, todo, partBytes);
  };

  // The server may answer with missing_parts; those are re-uploaded and COMPLETED sent again
  const markCompleted = async (fileId, item, info, partBytes) => {
    for (let round = 0; round < MAX_COMPLETE_ROUNDS; round++) {
      let missing = [];
      try {
        const data = await retry(() => updateUploadFileStatusApi({ ...api, fileId, status: 'COMPLETED' }));
        missing = data && Array.isArray(data.missing_parts) ? data.missing_parts.map(Number) : [];
        if (!missing.length) return;
      } catch (error) {
        missing = missingPartsOf(error);
        if (!missing.length) throw error;
      }
      if (info.uploadType !== UPLOAD_TYPE.MULTIPART) throw new Error('Server reported missing parts for a single-part file');
      await uploadParts(fileId, item, info, missing, partBytes);
    }
    throw new Error('Some parts are still missing after re-uploading them');
  };

  const uploadOne = async (fileId, item, initialInfo) => {
    let stage = 'upload';
    inFlightItems.set(fileId, item);
    try {
      const info = initialInfo || await retry(() => fetchFileInfo(fileId));
      const partBytes = new Map();
      if (info.uploadType === UPLOAD_TYPE.MULTIPART) await uploadMultipart(fileId, item, info, partBytes);
      else await uploadSingle(fileId, item, info);
      // The file only appears in the folder once marked completed
      stage = 'complete';
      await markCompleted(fileId, item, info, partBytes);
      inFlight.delete(fileId);
      inFlightItems.delete(fileId);
      complete(item);
    } catch (error) {
      inFlight.delete(fileId);
      inFlightItems.delete(fileId);
      if (stage === 'complete') {
        fail(item, `Uploaded, but the server could not be updated: ${error.message}`);
      } else {
        fail(item, error.message);
        // Best effort
        updateUploadFileStatusApi({ ...api, fileId, status: 'FAILED', errorMessage: error.message }).catch(() => {});
      }
    }
    publish();
  };

  publish(true);
  // Republish regularly so the speed drops to 0 when nothing is moving
  const ticker = setInterval(() => publish(true), 2000);
  const heartbeat = setInterval(() => {
    inFlight.forEach((bytes, fileId) => {
      reportUploadFileProgressApi({ ...api, fileId, uploadedBytes: bytes }).catch(() => {});
    });
  }, HEARTBEAT_INTERVAL_MS);
  try {
    // Register every batch first so a resume after a refresh can match all files
    const pending = [];
    for (let offset = 0; offset < items.length; offset += BATCH_SIZE) {
      const batch = items.slice(offset, offset + BATCH_SIZE);

      let registered;
      try {
        registered = await retry(() => registerUploadFilesApi({ ...api, files: batch.map(toFileMeta) }));
      } catch (error) {
        registeredFiles += batch.length;
        batch.forEach(item => fail(item, error.message));
        publish(true);
        continue;
      }
      registeredFiles += batch.length;

      // Match server rows to local files by normalized path, or by name when unique in the batch
      const byPath = new Map(registered.files.filter(row => row.path).map(row => [normalizeUploadPath(row.path), row]));
      const nameCounts = new Map();
      batch.forEach(item => nameCounts.set(toNFC(item.file.name), (nameCounts.get(toNFC(item.file.name)) || 0) + 1));
      const byName = new Map(registered.files.filter(row => row.name && nameCounts.get(toNFC(row.name)) === 1).map(row => [toNFC(row.name), row]));
      const rejected = new Map(registered.rejected.filter(entry => entry.id).map(entry => [normalizeUploadPath(entry.id), entry.reason]));

      const toUpload = [];
      batch.forEach(item => {
        const row = byPath.get(normalizeUploadPath(item.relativePath)) || byName.get(toNFC(item.file.name));
        if (row && row.fileId) {
          if (DONE_STATUSES.includes(row.status)) {
            complete(item);
          } else {
            // Failed files are reset and changed ones restarted before signing
            toUpload.push({ item, fileId: row.fileId, resetFailed: row.status === 'FAILED', restart: !!item.restart });
          }
        } else {
          const reason = rejected.get(normalizeUploadPath(item.relativePath)) || rejected.get(normalizeUploadPath(item.file.name));
          fail(item, reason ? `Rejected: ${reason}` : rejected.size ? 'Rejected by server' : 'File was not registered');
        }
      });
      publish(true);
      if (toUpload.length) pending.push(toUpload);
    }
    preparing = false;
    publish(true);

    for (const toUpload of pending) {
      const ready = [];
      await runPool(toUpload.map(pair => async () => {
        try {
          if (pair.resetFailed) await retry(() => updateUploadFileStatusApi({ ...api, fileId: pair.fileId, status: 'PENDING' }));
          if (pair.restart) await retry(() => restartUploadFileApi({ ...api, fileId: pair.fileId, fileSize: pair.item.file.size }));
          ready.push(pair);
        } catch (error) {
          fail(pair.item, `Could not reset the file for upload: ${error.message}`);
        }
      }), UPLOAD_CONCURRENCY);
      publish();
      if (!ready.length) continue;

      let signedBatch;
      try {
        signedBatch = await retry(() => getUploadFileUrlsApi({ ...api, fileIds: ready.map(pair => pair.fileId) }));
      } catch (error) {
        ready.forEach(pair => fail(pair.item, error.message));
        publish();
        continue;
      }
      const skippedIds = new Map(signedBatch.skipped.filter(entry => entry.id).map(entry => [entry.id, entry.reason]));

      await runPool(ready.map(({ item, fileId }) => async () => {
        if (skippedIds.has(fileId)) {
          progress.skippedFiles += 1;
          skipped.push({ path: item.relativePath, reason: skippedIds.get(fileId) || 'Skipped by server' });
          publish();
          return;
        }
        await uploadOne(fileId, item, signedBatch.urls[fileId]);
      }), UPLOAD_CONCURRENCY);
    }
  } finally {
    clearInterval(heartbeat);
    clearInterval(ticker);
    controls.delete(uploadId);
    updateLiveUpload(uploadId, { ...snapshot(), speed: 0, status: LIVE_STATUS.DONE });
  }
  return { ...progress, failures, skipped };
};
